import { ref, computed } from 'vue'
import {
  tutorialSteps,
  TUTORIAL_TYPES,
  isTutorialCompleted,
  markTutorialCompleted,
  resetTutorial,
  resetAllTutorials
} from '@/data/tutorialConfig'

/**
 * 教程管理 Composable
 */
export function useTutorial(tutorialType) {
  // 当前步骤索引
  const currentStepIndex = ref(0)

  // 是否激活教程
  const isActive = ref(false)

  // 获取当前教程的步骤
  const steps = computed(() => {
    return tutorialSteps[tutorialType] || []
  })

  // 当前步骤数据
  const currentStep = computed(() => {
    return steps.value[currentStepIndex.value] || {}
  })

  // 是否是最后一步
  const isLastStep = computed(() => {
    return currentStepIndex.value >= steps.value.length - 1
  })

  // 是否已完成
  const isCompleted = computed(() => {
    return isTutorialCompleted(tutorialType)
  })

  /**
   * 开始教程
   */
  function startTutorial() {
    if (steps.value.length === 0) {
      console.warn(`No steps found for tutorial type: ${tutorialType}`)
      return
    }

    currentStepIndex.value = 0
    isActive.value = true
  }

  /**
   * 下一步
   */
  function nextStep() {
    if (isLastStep.value) {
      finishTutorial()
    } else {
      currentStepIndex.value++
    }
  }

  /**
   * 上一步
   */
  function prevStep() {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value--
    }
  }

  /**
   * 跳转到指定步骤
   */
  function goToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < steps.value.length) {
      currentStepIndex.value = stepIndex
    }
  }

  /**
   * 跳过教程
   */
  function skipTutorial() {
    isActive.value = false
    currentStepIndex.value = 0
    markTutorialCompleted(tutorialType)
  }

  /**
   * 完成教程
   */
  function finishTutorial() {
    isActive.value = false
    currentStepIndex.value = 0
    markTutorialCompleted(tutorialType)
  }

  /**
   * 重置教程
   */
  function resetCurrentTutorial() {
    isActive.value = false
    currentStepIndex.value = 0
    resetTutorial(tutorialType)
  }

  /**
   * 检查目标元素是否存在
   */
  function checkTargetExists(target) {
    if (!target) return false
    return document.querySelector(target) !== null
  }

  /**
   * 等待目标元素出现
   */
  function waitForTarget(target, timeout = 5000) {
    return new Promise((resolve, reject) => {
      if (checkTargetExists(target)) {
        resolve(true)
        return
      }

      const startTime = Date.now()
      const interval = setInterval(() => {
        if (checkTargetExists(target)) {
          clearInterval(interval)
          resolve(true)
        } else if (Date.now() - startTime > timeout) {
          clearInterval(interval)
          reject(new Error(`Target ${target} not found within ${timeout}ms`))
        }
      }, 100)
    })
  }

  return {
    // 状态
    currentStepIndex,
    isActive,
    steps,
    currentStep,
    isLastStep,
    isCompleted,

    // 方法
    startTutorial,
    nextStep,
    prevStep,
    goToStep,
    skipTutorial,
    finishTutorial,
    resetCurrentTutorial,
    checkTargetExists,
    waitForTarget
  }
}

/**
 * 全局教程管理
 */
export function useGlobalTutorial() {
  /**
   * 重置所有教程
   */
  function resetAll() {
    resetAllTutorials()
  }

  /**
   * 检查教程是否已完成
   */
  function checkCompleted(tutorialType) {
    return isTutorialCompleted(tutorialType)
  }

  /**
   * 获取所有教程类型
   */
  function getAllTutorialTypes() {
    return Object.values(TUTORIAL_TYPES)
  }

  return {
    resetAll,
    checkCompleted,
    getAllTutorialTypes,
    TUTORIAL_TYPES
  }
}
